import React from "react";
import { Loader2, ShieldCheck } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import type { SignupConsentDocument } from "@/types/consultant-application";

/**
 * Renders `text` with the first occurrence of `link_text` as an external link.
 * The wording is the backend's, verbatim.
 */
const DocumentText: React.FC<{ document: SignupConsentDocument }> = ({ document }) => {
  const { text, link_text: linkText, link_url: linkUrl } = document;
  const at = linkText && linkUrl ? text.indexOf(linkText) : -1;
  if (!linkText || !linkUrl || at < 0) {
    return <>{text}</>;
  }
  return (
    <>
      {text.slice(0, at)}
      <a
        href={linkUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-block py-1 -my-1 font-medium text-blue-700 underline underline-offset-2 hover:text-blue-800"
      >
        {linkText}
      </a>
      {text.slice(at + linkText.length)}
    </>
  );
};

interface SignupAcknowledgementsProps {
  documents: SignupConsentDocument[] | null;
  isLoading: boolean;
  error: string | null;
  accepted: Record<string, boolean>;
  disabled?: boolean;
  onChange: (documentKey: string, checked: boolean) => void;
  onRetry: () => void;
}

/**
 * The required privacy policy and confidentiality acknowledgements on the
 * last signup step (backend#863). Submit stays disabled until every box is
 * ticked; the backend enforces the same rule.
 */
export const SignupAcknowledgements: React.FC<SignupAcknowledgementsProps> = ({
  documents,
  isLoading,
  error,
  accepted,
  disabled = false,
  onChange,
  onRetry,
}) => {
  if (error) {
    return (
      <Alert variant="destructive">
        <AlertDescription className="flex items-center justify-between gap-4">
          <span>{error}</span>
          <Button type="button" variant="outline" size="sm" onClick={onRetry}>
            Try again
          </Button>
        </AlertDescription>
      </Alert>
    );
  }

  if (isLoading || documents === null) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        Loading privacy statements…
      </div>
    );
  }

  return (
    <fieldset className="space-y-3 rounded-md border border-gray-200 p-4">
      <legend className="flex items-center gap-2 px-1 text-sm font-medium">
        <ShieldCheck className="h-4 w-4" />
        Privacy and confidentiality *
      </legend>
      {documents.map(document => {
        const id = `ack-${document.document}`;
        return (
          <div key={document.document} className="flex items-start gap-3">
            <input
              id={id}
              type="checkbox"
              className="mt-0.5 h-4 w-4 shrink-0 cursor-pointer accent-primary"
              checked={Boolean(accepted[document.document])}
              disabled={disabled}
              onChange={event => onChange(document.document, event.target.checked)}
            />
            <label htmlFor={id} className="text-sm leading-relaxed text-gray-700">
              <span className="sr-only">{document.title}: </span>
              <DocumentText document={document} />
            </label>
          </div>
        );
      })}
    </fieldset>
  );
};
